<!-- CategoryPage.vue -->
<template>
    <div class="body store-app" :class="{ 'store-layout-large': storeLayout === 'large' }">
        <Navbar 
            :showBackButton="true" 
            @back="goBack"
            :title="categoryName"
        />
        
        <!-- Shopping Cart Component -->
        <ShoppingCart 
            ref="shoppingCart"
            @cart-updated="handleCartUpdate"
            @checkout="handleCheckout"
        />
        
        <!-- Category Header -->
        <div class="category-header">
            <div class="category-info">
                <!-- Show category image if available -->
                <div class="category-image-wrapper" v-if="categoryImage">
                    <img 
                        :src="getImageSrc(categoryImage)" 
                        :alt="categoryName"
                        class="category-image"
                        @error="handleImageError"
                    />
                </div>
                <div class="category-icon-large" v-else>📦</div>
                <div class="category-details">
                    <h1>{{ categoryName }}</h1>
                    <p>{{ totalProducts }} محصول در این دسته‌بندی</p>
                </div>
            </div>
            
            <div class="category-actions">
                <div class="search-container">
                    <input 
                        type="text" 
                        v-model="searchQuery"
                        @input="handleSearchInCategory"
                        placeholder="جستجو در این دسته..."
                        class="search-input"
                    />
                    <span class="search-icon">🔍</span>
                </div>
                
                <button class="sort-btn" @click="toggleSortMenu">
                    <span>مرتب‌سازی</span>
                    <span class="sort-icon">▼</span>
                </button>
                
                <!-- Sort Menu -->
                <div v-if="showSortMenu" class="sort-menu" @click.stop>
                    <div 
                        v-for="option in sortOptions" 
                        :key="option.value"
                        class="sort-option"
                        :class="{ active: currentSort === option.value }"
                        @click="sortProducts(option.value)"
                    >
                        {{ option.label }}
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Products Section - Horizontal Layout -->
        <div class="products-section">
            <div class="section-title1">
                <h3>{{ categoryName }}</h3>
                <div class="products-count">
                    {{ totalProducts }} محصول
                </div>
            </div>
            
            <!-- Loading State -->
            <div v-if="loading" class="loading-state">
                <div class="spinner"></div>
                {{ $t('sales.kalaStore.products.loading') }}
            </div>
            
            <!-- Products List - Horizontal Layout -->
            <div v-else-if="products.length > 0" class="products-list">
                <div 
                    v-for="product in products" 
                    :key="product.SubCode"
                    class="product-list-item"
                >
                    <div class="product-content">
                        <div class="product-details-left">
                            <div class="product-title" style="text-align: right;" @click="viewProductDetails(product)">{{ product.Name }}</div>
                            <div class="product-description" style="text-align: right;">
                                {{ product.Comment_fa || '' }}
                            </div>
                            <div class="product-meta-row">
                                <span class="product-code">
                                    کد: {{ product.SubCode || product.Commodity_Code }}
                                </span>
                                <span class="product-unit">
                                    واحد: {{ getProductUnit(product) }}
                                </span>
                                <span v-if="product.StockQuantity > 0" class="product-stock-info" :class="{ 'low-stock': product.StockQuantity < 10 }">
                                    موجودی  : {{ product.StockQuantity || 0 }} {{ product.Vahed1 || '' }}
                                </span>
                            </div>
                        </div>
                        
                        <div class="product-actions-right">
                            <!-- Cart Controls -->
                            <div class="cart-controls">
                                <template v-if="isInCart(product)">
                                    <button 
                                        class="qty-control-btn decrement"
                                        @click="decrementQuantity(product)"
                                        :disabled="getCartItemQuantity(product) <= 1"
                                    >
                                        −
                                    </button>
                                    
                                    <input 
                                        type="number" 
                                        :value="getCartItemQuantity(product)"
                                        min="1"
                                        :max="product.StockQuantity || 999"
                                        @input="updateQuantityInput(product, $event)"
                                        class="qty-input-cart"
                                    />
                                    
                                    <button 
                                        class="qty-control-btn increment"
                                        @click="incrementQuantity(product)"
                                    >
                                        +
                                    </button>
                                    
                                    <button 
                                        class="remove-from-cart-btn"
                                        @click="removeFromCart(product)"
                                        title="Remove from cart"
                                    >
                                        ×
                                    </button>
                                </template>
                                <template v-else>
                                    <button 
                                        class="add-to-cart-btn"
                                        @click="addToCart(product)"
                                    >
                                        + {{ $t('sales.kalaStore.products.addToCart') }}
                                    </button>
                                </template>
                                <div v-if="product.Fe1 > 0" class="product-price">
                                    هر {{ product.Vahed1 }} {{ formatCurrency(parseFloat(product.Fe1 || 0)) }} {{ $t('sales.kalaStore.common.rial') }} 
                                </div>
                            </div>
                            
                            <!-- Note Input for items in cart -->
                            <div v-if="isInCart(product)" class="cart-note-input">
                                <input 
                                    type="text"
                                    :value="getCartItemNote(product)"
                                    @input="updateNoteInput(product, $event)"
                                    :placeholder="'توضیحات برای ' + product.Name"
                                    class="note-input-field"
                                />
                                <span class="note-icon">📝</span>
                            </div>
                            
                        </div>
                            
                        <div v-if="product.Image1 != 'No Image'" class="product-image-right product-gallery-frame" @click="viewProductDetails(product)">
                            <img :src="getImageSrc(getProductImages(product)[getProductImageIndex(product)] || getProductImage(product))" :alt="product.Name" @click.stop="openImageZoom(getProductImages(product)[getProductImageIndex(product)] || product.Image1)">
                            <template v-if="getProductImages(product).length > 1">
                                <button class="gallery-arrow gallery-prev" type="button" aria-label="تصویر قبلی" @click.stop="stepProductImage(product, -1)">‹</button>
                                <button class="gallery-arrow gallery-next" type="button" aria-label="تصویر بعدی" @click.stop="stepProductImage(product, 1)">›</button>
                                <span class="gallery-count">{{ getProductImageIndex(product) + 1 }} / {{ getProductImages(product).length }}</span>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Empty State -->
            <div v-else class="empty-products">
                <div class="empty-icon">📭</div>
                <h3>محصولی یافت نشد</h3>
                <p>در این دسته‌بندی محصولی وجود ندارد</p>
                <button class="back-to-categories" @click="goBack">
                    بازگشت به دسته‌بندی‌ها
                </button>
            </div>
            
            <!-- Pagination -->
            <div v-if="totalPages > 1" class="pagination">
                <button 
                    @click="prevPage" 
                    :disabled="currentPage === 1"
                    class="pagination-btn"
                >
                    قبلی
                </button>
                
                <div class="page-numbers">
                    <span 
                        v-for="page in visiblePages" 
                        :key="page"
                        @click="goToPage(page)"
                        :class="{ 
                            'page-number': true, 
                            'active': page === currentPage,
                            'disabled': page === '...'
                        }"
                    >
                        {{ page }}
                    </span>
                </div>
                
                <button 
                    @click="nextPage" 
                    :disabled="currentPage === totalPages"
                    class="pagination-btn"
                >
                    بعدی
                </button>
            </div>
        </div>
        
        <!-- Product Details Modal -->
        <div v-if="showProductModal" class="product-modal-overlay" @click="closeProductModal">
            <div class="product-modal-content" @click.stop>
                <div class="product-modal-header">
                    <h3>{{ $t('sales.kalaStore.productDetails.title') }}</h3>
                    <button class="close-modal-btn" @click="closeProductModal">×</button>
                </div>
                
                <div class="product-modal-body" v-if="selectedProduct">
                    <div v-if="selectedProduct.Image1 != 'No Image'" class="product-modal-image product-gallery-frame">
                        <img :src="getImageSrc(getModalImage() || getProductImage(selectedProduct))" :alt="$t('sales.kalaStore.products.productImage')" @click="openImageZoom(getModalImage() || selectedProduct.Image1)">
                        <template v-if="getProductImages(selectedProduct).length > 1">
                            <button class="gallery-arrow gallery-prev" type="button" aria-label="تصویر قبلی" @click.stop="stepModalImage(-1)">‹</button>
                            <button class="gallery-arrow gallery-next" type="button" aria-label="تصویر بعدی" @click.stop="stepModalImage(1)">›</button>
                            <span class="gallery-count">{{ modalImageIndex + 1 }} / {{ getProductImages(selectedProduct).length }}</span>
                        </template>
                    </div>
                    
                    <div class="product-modal-info">
                        <h2>{{ selectedProduct.Name }}</h2>
                        
                        <div class="product-modal-price">
                            {{ formatCurrency(parseFloat(selectedProduct.Fe1 || 0)) }} {{ $t('sales.kalaStore.common.rial') }}
                        </div>
                        
                        <div class="product-modal-stock" :class="{ 
                            'in-stock': selectedProduct.StockQuantity > 0,
                            'out-of-stock': !selectedProduct.StockQuantity || selectedProduct.StockQuantity <= 0
                        }">
                            <span class="stock-icon" v-if="selectedProduct.StockQuantity > 0">✓</span>
                            <span class="stock-icon" v-else>✕</span>
                            {{ $t('sales.kalaStore.productDetails.status') }}: 
                            <span v-if="selectedProduct.StockQuantity > 0">
                                {{ $t('sales.kalaStore.productDetails.inStock') }} ({{ selectedProduct.StockQuantity }} {{ $t('sales.kalaStore.common.items') }})
                            </span>
                            <span v-else>{{ $t('sales.kalaStore.productDetails.outOfStock') }}</span>
                        </div>
                        
                        <div class="product-modal-details">
                            <div class="detail-row">
                                <span v-if="selectedProduct.SubCode" class="detail-label">{{ $t('sales.kalaStore.productDetails.productCode') }}</span>
                                <span v-if="selectedProduct.SubCode" class="detail-value">{{ selectedProduct.SubCode }}</span>

                                <span v-if="selectedProduct.Commodity_Code" class="detail-label">{{ $t('sales.kalaStore.productDetails.manualCode') }}</span>
                                <span v-if="selectedProduct.Commodity_Code" class="detail-value">{{ selectedProduct.Commodity_Code }}</span>

                                <span v-if="selectedProduct.BarCode" class="detail-label">{{ $t('sales.kalaStore.productDetails.barcode') }}</span>
                                <span v-if="selectedProduct.BarCode" class="detail-value">{{ selectedProduct.BarCode }}</span>
                            </div>
                            <div class="detail-row">
                                <span class="detail-label">{{ $t('sales.kalaStore.productDetails.unit') }}</span>
                                <span class="detail-value">{{ selectedProduct.Vahed1 }}</span>
                            </div>
                        </div>
                        
                        <div class="product-modal-description" v-if="selectedProduct.Commodity_comment">
                            <p>{{ selectedProduct.Commodity_comment }}</p>
                        </div>
                        
                        <!-- Add to Cart Section -->
                        <div v-if="selectedProduct.StockQuantity > 0" class="add-to-cart-section">
                            <div class="quantity-controls">
                                <button class="qty-btn minus" @click="modalQuantity > 1 ? modalQuantity-- : null">
                                    −
                                </button>
                                <input 
                                    type="number" 
                                    v-model.number="modalQuantity"
                                    min="1"
                                    :max="selectedProduct.StockQuantity"
                                    class="qty-input"
                                />
                                <button class="qty-btn plus" @click="modalQuantity < selectedProduct.StockQuantity ? modalQuantity++ : null">
                                    +
                                </button>
                            </div>
                            
                            <button 
                                class="add-to-cart-modal-btn"
                                @click="addToCartWithQuantity(selectedProduct, modalQuantity)"
                            >
                                <span v-if="isInCart(selectedProduct)">
                                    ✓ {{ $t('sales.kalaStore.productDetails.updateCart') }}
                                </span>
                                <span v-else>
                                    + {{ $t('sales.kalaStore.productDetails.addToCart') }}
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Checkout/Invoice Modal -->
        <div v-if="showCheckoutModal" class="checkout-modal-overlay" @click="closeCheckoutModal">
            <div class="checkout-modal-content" @click.stop>
                <div class="checkout-modal-header">
                    <h3>🛒 تسویه حساب</h3>
                    <button class="close-checkout-btn" @click="closeCheckoutModal">×</button>
                </div>
                
                <div class="checkout-modal-body">
                    <div class="invoice-summary">
                        <h4>خلاصه فاکتور</h4>
                        <div class="summary-card">
                            <div class="summary-row">
                                <span>تعداد اقلام:</span>
                                <span>{{ cartItems.length }} قلم</span>
                            </div>
                            <div class="summary-row total">
                                <span>مبلغ کل:</span>
                                <span class="amount">{{ formatCurrency(calculateSubTotal()) }} ریال</span>
                            </div>
                        </div>
                    </div>
                    
                    <div class="cart-review">
                        <h4>اقلام سبد خرید</h4>
                        <div class="cart-review-items">
                            <div v-for="(item, index) in cartItems" :key="index" class="cart-review-item">
                                <div class="item-info">
                                    <span class="item-name">{{ item.name }}</span>
                                    <span class="item-quantity">{{ item.quantity }} × {{ formatCurrency(item.price) }}</span>
                                </div>
                                <div class="item-total">
                                    {{ formatCurrency(item.price * item.quantity) }} ریال
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="checkout-modal-footer">
                    <button class="cancel-btn" @click="closeCheckoutModal" :disabled="checkoutLoading">
                        انصراف
                    </button>
                    <button class="submit-invoice-btn" @click="submitInvoice" :disabled="checkoutLoading">
                        <span v-if="checkoutLoading" class="spinner-small"></span>
                        <span v-else>✓ ثبت فاکتور</span>
                    </button>
                </div>
            </div>
        </div>
        <ImageZoomOverlay v-model="imageZoomOpen" :src="imageZoomSrc" alt="تصویر کالا" />
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { SalesService } from '@/services/api'
import Navbar from '@/components/Navbar.vue'
import ShoppingCart from '@/components/ShoppingCartFloating.vue'
import ImageZoomOverlay from '@/components/ImageZoomOverlay.vue'
import moment from 'moment-jalaali'

export default {
    name: 'CategoryPage',
    components: { Navbar, ShoppingCart, ImageZoomOverlay },
    setup() {
        const route = useRoute()
        const router = useRouter()
        const authStore = useAuthStore()
        const shoppingCart = ref(null)
        
        // State
        const products = ref([])
        const categoryName = ref('')
        const categoryId = ref('')
        const loading = ref(false)
        const searchQuery = ref('')
        const currentPage = ref(1)
        const pageSize = ref(12)
        const totalProducts = ref(0)
        const totalPages = ref(1)
        const showSortMenu = ref(false)
        const currentSort = ref('name_asc')
        const showProductModal = ref(false)
        const selectedProduct = ref(null)
        const modalQuantity = ref(1)
        const showCheckoutModal = ref(false)
        const checkoutLoading = ref(false)
        const categoryImage = ref(null)
        const storeLayout = ref('minimal')
        const imageZoomOpen = ref(false)
        const imageZoomSrc = ref('')
        const productImageIndexes = ref({})
        const modalImageIndex = ref(0)
        
        // Cart items from component
        const cartItems = ref([])
        
        // Sort options
        const sortOptions = [
            { value: 'name_asc', label: 'نام (الفبا)' },
            { value: 'name_desc', label: 'نام (معکوس)' },
            { value: 'price_asc', label: 'قیمت (از کم به زیاد)' },
            { value: 'price_desc', label: 'قیمت (از زیاد به کم)' },
            { value: 'stock_desc', label: 'موجودی (بیشترین)' }
        ]
        
        // Computed
        const cartTotalItems = computed(() => {
            return cartItems.value.reduce((total, item) => total + item.quantity, 0)
        })
        
        const visiblePages = computed(() => {
            const pages = []
            const maxVisible = 5
            
            if (totalPages.value <= maxVisible) {
                for (let i = 1; i <= totalPages.value; i++) {
                    pages.push(i)
                }
            } else {
                pages.push(1)
                if (currentPage.value > 3) pages.push('...')
                
                const start = Math.max(2, currentPage.value - 1)
                const end = Math.min(totalPages.value - 1, currentPage.value + 1)
                for (let i = start; i <= end; i++) pages.push(i)
                
                if (currentPage.value < totalPages.value - 2) pages.push('...')
                pages.push(totalPages.value)
            }
            return pages
        })
        
        // Methods
        
        const addToCart = (product) => {
            if (shoppingCart.value) {
                shoppingCart.value.addToCart(product)
            }
        }
        
        const addToCartWithQuantity = (product, quantity) => {
            if (shoppingCart.value) {
                shoppingCart.value.addToCart(product, quantity)
                closeProductModal()
            }
        }
        
        const isInCart = (product) => {
            return shoppingCart.value ? shoppingCart.value.isInCart(product) : false
        }

        // Get quantity of a specific product in cart
        const getCartItemQuantity = (product) => {
            if (shoppingCart.value) {
                const cartItems = shoppingCart.value.cartItems
                const itemKey = product.SubCode
                const cartItem = cartItems.find(item => item.kala_id === itemKey)
                return cartItem ? cartItem.quantity : 0
            }
            return 0
        }

        // Increment quantity
        const incrementQuantity = (product) => {
            if (shoppingCart.value) {
                shoppingCart.value.addToCart(product, 1)
            }
        }

        // Decrement quantity
        const decrementQuantity = (product) => {
            if (shoppingCart.value) {
                const currentQty = getCartItemQuantity(product)
                if (currentQty > 0) {
                    const newQty = currentQty - 1
                    shoppingCart.value.updateCartItemQuantity(product, newQty)
                }
            }
        }

        // Remove from cart
        const removeFromCart = (product) => {
            if (shoppingCart.value) {
                shoppingCart.value.updateCartItemQuantity(product, 0)
            }
        }

        const getProductUnit = (product) => {
            if (product.Kala4 === "1.0") {
                return product.Vahed2
            }
            return product.Vahed1
        }
        
        const getImageSrc = (image) => {
            if (!image || image === 'No Image') return getProductImage()
            const value = String(image).trim()
            if (/^(https?:)?\/\//i.test(value) || value.startsWith('data:') || value.startsWith('/')) return value
            return `data:image/jpeg;base64,${value}`
        }

        const openImageZoom = (image) => {
            const source = getImageSrc(image)
            if (!source) return
            imageZoomSrc.value = source
            imageZoomOpen.value = true
        }

        const getProductImages = (product) => [...new Set([
            ...(Array.isArray(product?.Images) ? product.Images : []),
            product?.Image1,
            product?.Image2,
        ].filter(value => value && value !== 'No Image').map(value => String(value).trim()))]

        const getProductImageIndex = (product) => productImageIndexes.value[String(product?.SubCode || '')] || 0
        const stepProductImage = (product, direction) => {
            const images = getProductImages(product)
            if (images.length < 2) return
            const key = String(product.SubCode || '')
            productImageIndexes.value[key] = (getProductImageIndex(product) + direction + images.length) % images.length
        }
        const getModalImage = () => getProductImages(selectedProduct.value)[modalImageIndex.value] || ''
        const stepModalImage = (direction) => {
            const count = getProductImages(selectedProduct.value).length
            if (count > 1) modalImageIndex.value = (modalImageIndex.value + direction + count) % count
        }

        const fetchCategoryProducts = async () => {
            if (!authStore.isAuthenticated) return
            
            loading.value = true
            
            try {
                const catId = route.params.id
                const searchTerm = searchQuery.value || ''
                
                const response = await SalesService.getKalaByCategory(
                    catId,
                    currentPage.value,
                    pageSize.value,
                    searchTerm
                )
                
                if (response.data.success) {
                    products.value = response.data.children_kala
                    totalProducts.value = response.data.total_count
                    totalPages.value = response.data.total_pages
                    
                    // Get parent category data (first item in parent_kala array)
                    if (response.data.parent_kala && response.data.parent_kala.length > 0) {
                        const parent = response.data.parent_kala[0]
                        categoryName.value = parent.Name || route.query.name || 'دسته‌بندی'
                        
                        // Store parent image for the header
                        if (parent.Image1) {
                            categoryImage.value = parent.Image1
                        }
                    }
                    
                    // Apply sorting after fetching
                    sortProductsList()
                }
            } catch (error) {
                console.error('Error fetching category products:', error)
            } finally {
                loading.value = false
            }
        }
        
        const sortProductsList = () => {
            const sorted = [...products.value]
            
            switch (currentSort.value) {
                case 'name_asc':
                    sorted.sort((a, b) => (a.Name || '').localeCompare(b.Name || ''))
                    break
                case 'name_desc':
                    sorted.sort((a, b) => (b.Name || '').localeCompare(a.Name || ''))
                    break
                case 'price_asc':
                    sorted.sort((a, b) => parseFloat(a.Fe1 || 0) - parseFloat(b.Fe1 || 0))
                    break
                case 'price_desc':
                    sorted.sort((a, b) => parseFloat(b.Fe1 || 0) - parseFloat(a.Fe1 || 0))
                    break
                case 'stock_desc':
                    sorted.sort((a, b) => (b.StockQuantity || 0) - (a.StockQuantity || 0))
                    break
            }
            
            products.value = sorted
        }
        
        const sortProducts = (sortValue) => {
            currentSort.value = sortValue
            sortProductsList()
            showSortMenu.value = false
        }
        
        const toggleSortMenu = () => {
            showSortMenu.value = !showSortMenu.value
        }
        
        const handleSearchInCategory = () => {
            currentPage.value = 1
            fetchCategoryProducts()
        }
        
        const viewProductDetails = (product) => {
            selectedProduct.value = product
            modalImageIndex.value = 0
            modalQuantity.value = 1
            showProductModal.value = true
        }
        
        const closeProductModal = () => {
            showProductModal.value = false
            selectedProduct.value = null
        }
        
        const getProductImage = () => {
            return require('@/assets/Icons/product.png')
        }
        
        const formatCurrency = (amount) => {
            return new Intl.NumberFormat('fa-IR').format(amount)
        }
        
        const prevPage = () => {
            if (currentPage.value > 1) {
                currentPage.value--
                fetchCategoryProducts()
                window.scrollTo({ top: 0, behavior: 'smooth' })
            }
        }
        
        const nextPage = () => {
            if (currentPage.value < totalPages.value) {
                currentPage.value++
                fetchCategoryProducts()
                window.scrollTo({ top: 0, behavior: 'smooth' })
            }
        }
        
        const goToPage = (page) => {
            if (page !== '...' && page !== currentPage.value) {
                currentPage.value = page
                fetchCategoryProducts()
                window.scrollTo({ top: 0, behavior: 'smooth' })
            }
        }
        
        const goBack = () => {
            router.push('/sales/kala-store')
        }
        
        const closeCheckoutModal = () => {
            showCheckoutModal.value = false
        }
        
        const calculateSubTotal = () => {
            return cartItems.value.reduce((total, item) => total + (item.price * item.quantity), 0)
        }
        
        const submitInvoice = async () => {
            if (cartItems.value.length === 0) {
                alert('سبد خرید شما خالی است')
                return
            }
            
            checkoutLoading.value = true
            
            try {
                // Prepare invoice data
                const invoiceData = {
                    Header: {
                        Ac: 2,
                        Act: 316,
                        Dates: moment().format('jYYYY/jMM/jDD'),
                        Ei5: parseInt(JSON.parse(localStorage.getItem('user') || '{}').subcode) || 55563,
                        Operator: parseInt(JSON.parse(localStorage.getItem('user') || '{}').id) || 33,
                        Arz: 1,
                        Erja: 0,
                        Duration: 0,
                        Saresid: moment().add(1, 'day').format('jYYYY/jMM/jDD'),
                        Comments: "",
                        Pelak: "",
                        In_Oto: "",
                        In_Bar: "",
                        In_Driver: 0,
                        In_Fe: 0,
                        To_Time: 0,
                        Rec_num: 0,
                        Rec_Date: ""
                    },
                    Body: cartItems.value.map(item => ({
                        Codeing: 1,
                        Commodity_code: parseInt(item.kala_id) || 0,
                        value1: parseFloat(item.quantity) || 0,
                        Fe: parseFloat(item.price) || 0,
                        Precent: 0.0,
                        Discount: 0.0,
                        DScot: 0.0,
                        Scot: 0.0,
                        DToll: 0.0,
                        Toll: 0.0,
                        Depot: 6
                    })),
                    Dec: [],
                    Inc: [],
                    Payment: []
                }
                
                const response = await SalesService.saveInvoice(invoiceData)
                
                if (response.data.success) {
                    alert('فاکتور با موفقیت ثبت شد!')
                    if (shoppingCart.value) {
                        shoppingCart.value.clearCart()
                    }
                    showCheckoutModal.value = false
                } else {
                    alert(`خطا در ثبت فاکتور: ${response.data.message}`)
                }
            } catch (error) {
                console.error('Error submitting invoice:', error)
                alert('خطا در ارتباط با سرور. لطفا دوباره تلاش کنید.')
            } finally {
                checkoutLoading.value = false
            }
        }
        
        // Close sort menu when clicking outside
        const handleClickOutside = (event) => {
            if (!event.target.closest('.category-actions')) {
                showSortMenu.value = false
            }
        }

        // Update quantity from input field
        const updateQuantityInput = (product, event) => {
            if (shoppingCart.value) {
                let newQuantity = event.target.value
                
                // Validate input
                if (isNaN(newQuantity) || newQuantity < 1) {
                    newQuantity = 1
                }
                
                // if (newQuantity > product.StockQuantity) {
                //     newQuantity = product.StockQuantity
                // }
                
                // Update the cart
                shoppingCart.value.updateCartItemQuantity(product, newQuantity)
            }
        }

        // Get note for a specific product in cart
        const getCartItemNote = (product) => {
            if (shoppingCart.value) {
                const cartItems = shoppingCart.value.cartItems
                const itemKey = product.SubCode
                const cartItem = cartItems.find(item => item.kala_id === itemKey)
                return cartItem ? cartItem.note || '' : ''
            }
            return ''
        }

        // Update note for a product in cart
        const updateNoteInput = (product, event) => {
            if (shoppingCart.value) {
                const note = event.target.value
                const itemKey = product.SubCode
                const cartItems = shoppingCart.value.cartItems
                const cartItem = cartItems.find(item => item.kala_id === itemKey)
                
                if (cartItem) {
                    cartItem.note = note
                    // Save to localStorage
                    localStorage.setItem('shoppingCart', JSON.stringify(cartItems))
                    // Emit update event
                    shoppingCart.value.$emit('cart-updated', {
                        items: cartItems,
                        totalItems: shoppingCart.value.cartTotalItems,
                        totalAmount: shoppingCart.value.cartTotalAmount
                    })
                }
            }
        }

        const handleImageError = (event) => {
            event.target.style.display = 'none'
            // Show fallback icon
            const wrapper = event.target.closest('.category-image-wrapper')
            if (wrapper) {
                const icon = document.createElement('div')
                icon.className = 'category-icon-large'
                icon.textContent = '📦'
                wrapper.appendChild(icon)
            }
        }
        
        // Lifecycle
        onMounted(() => {
            document.addEventListener('click', handleClickOutside)
            
            // Get category data from route params
            categoryId.value = route.params.id
            categoryName.value = route.query.name || 'دسته‌بندی'
            
            if (authStore.isAuthenticated) {
                fetchCategoryProducts()
            }
            SalesService.getStoreLayout()
                .then(response => { storeLayout.value = response.data?.store_layout === 'large' ? 'large' : 'minimal' })
                .catch(error => console.warn('Could not load store layout; using minimal layout.', error))
        })
        
        return {
            products,
            categoryName,
            loading,
            searchQuery,
            currentPage,
            totalPages,
            totalProducts,
            cartTotalItems,
            visiblePages,
            showSortMenu,
            currentSort,
            sortOptions,
            showProductModal,
            selectedProduct,
            modalQuantity,
            showCheckoutModal,
            checkoutLoading,
            cartItems,
            shoppingCart,
            handleSearchInCategory,
            addToCart,
            addToCartWithQuantity,
            isInCart,
            getCartItemQuantity,
            incrementQuantity,
            decrementQuantity,
            removeFromCart,
            getProductUnit,
            viewProductDetails,
            closeProductModal,
            getProductImage,
            formatCurrency,
            prevPage,
            nextPage,
            goToPage,
            goBack,
            toggleSortMenu,
            sortProducts,
            closeCheckoutModal,
            calculateSubTotal,
            submitInvoice,
            updateQuantityInput,
            getCartItemNote,
            updateNoteInput,
            categoryImage,
            storeLayout,
            imageZoomOpen,
            imageZoomSrc,
            getImageSrc,
            openImageZoom,
            getProductImages,
            getProductImageIndex,
            stepProductImage,
            modalImageIndex,
            getModalImage,
            stepModalImage,
            handleImageError,
        }
    }
}
</script>

<style scoped>
.body {
    background: var(--bg);
    min-height: 100vh;
    margin-bottom: 40px;
}

.category-header {
    background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
    padding: 30px 20px;
    margin-bottom: 30px;
    border-radius: 0 0 20px 20px;
}

.category-info {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 20px;
}

.category-icon-large {
    font-size: 60px;
    background: rgba(255, 255, 255, 0.2);
    width: 100px;
    height: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 20px;
}

.category-details h1 {
    color: white;
    margin: 0 0 10px 0;
    font-size: 28px;
}

.category-details p {
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
    font-size: 14px;
}

.category-actions {
    display: flex;
    gap: 10px;
    align-items: center;
    position: relative;
}

.search-container {
    flex: 1;
    position: relative;
}

.search-input {
    width: 100%;
    padding: 12px 40px 12px 15px;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    direction: rtl;
    background: var(--card-bg);
    color: var(--text);
}

.search-input::placeholder {
    color: var(--subtext);
}

.search-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 18px;
    color: var(--subtext);
}

.sort-btn {
    background: rgba(255, 255, 255, 0.2);
    border: none;
    padding: 12px 20px;
    border-radius: 10px;
    color: white;
    cursor: pointer;
    display: flex;
    gap: 8px;
    align-items: center;
    transition: all 0.3s ease;
}

.sort-btn:hover {
    background: rgba(255, 255, 255, 0.3);
}

.sort-menu {
    position: absolute;
    top: 100%;
    right: 0;
    background: var(--card-bg);
    border-radius: 10px;
    box-shadow: 0 5px 20px var(--shadow-2);
    min-width: 200px;
    z-index: 100;
    margin-top: 5px;
    overflow: hidden;
    border: 1px solid var(--card-border);
}

.sort-option {
    padding: 12px 15px;
    cursor: pointer;
    transition: background 0.2s ease;
    text-align: right;
    color: var(--text);
}

.sort-option:hover {
    background: var(--ring);
}

.sort-option.active {
    background: var(--primary);
    color: white;
}

/* Products Section */
.products-section {
    padding: 20px;
    margin-bottom: 45px;
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
}

.section-title1 {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.section-title1 h3 {
    font-size: 20px;
    font-weight: 600;
    color: var(--heading);
    margin: 0;
}

.products-count {
    font-size: 14px;
    color: var(--subtext);
    background: var(--bg);
    padding: 4px 12px;
    border-radius: 20px;
}

/* Products List - Horizontal Layout */
.products-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.product-list-item {
    background: var(--card-bg);
    border-radius: 12px;
    padding: 16px 20px;
    box-shadow: 0 2px 8px var(--shadow-1);
    transition: all 0.3s ease;
    border: 1px solid var(--card-border);
}

.product-list-item:hover {
    box-shadow: 0 4px 16px var(--shadow-2);
    transform: translateY(-2px);
}

.product-content {
    display: flex;
    align-items: center;
    gap: 16px;
    direction: ltr;
}

.product-details-left {
    flex: 1;
    min-width: 0;
}

.product-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--heading);
    margin-bottom: 4px;
    text-align: right;
}

.product-description {
    font-size: 13px;
    color: var(--subtext);
    margin-bottom: 4px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-align: right;
}

.product-meta-row {
    display: flex;
    gap: 16px;
    font-size: 12px;
    color: var(--muted);
    flex-wrap: wrap;
}

.product-meta-row span {
    display: flex;
    align-items: center;
    gap: 4px;
}

.product-stock-info.low-stock {
    color: var(--orange-1);
    font-weight: 600;
}

.product-actions-right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
    min-width: 140px;
}

.product-price {
    font-size: 18px;
    font-weight: 700;
    color: var(--green);
}

.product-image-right {
    width: 80px;
    height: 80px;
    flex-shrink: 0;
    border-radius: 12px;
    overflow: hidden;
    background: var(--bg);
    border: 1px solid var(--card-border);
}

.product-image-right img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

/* Cart Controls */
.cart-controls {
    display: flex;
    align-items: center;
    gap: 6px;
}

.qty-control-btn {
    width: 28px;
    height: 28px;
    border: 2px solid var(--card-border);
    border-radius: 6px;
    background: var(--card-bg);
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text);
}

.qty-control-btn:hover:not(:disabled) {
    background: var(--bg);
    border-color: var(--primary);
}

.qty-control-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.qty-control-btn.increment {
    color: var(--green);
}

.qty-control-btn.increment:hover:not(:disabled) {
    background: rgba(16, 185, 129, 0.1);
    border-color: var(--green);
}

.qty-control-btn.decrement {
    color: var(--orange-1);
}

.qty-control-btn.decrement:hover:not(:disabled) {
    background: rgba(249, 115, 22, 0.1);
    border-color: var(--orange-1);
}

.cart-quantity {
    min-width: 24px;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    color: var(--heading);
}

.remove-from-cart-btn {
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 50%;
    background: rgba(249, 115, 22, 0.1);
    color: var(--orange-1);
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    line-height: 1;
}

.remove-from-cart-btn:hover {
    background: rgba(249, 115, 22, 0.2);
    transform: scale(1.1);
}

.add-to-cart-btn {
    padding: 6px 16px;
    background: var(--primary);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    white-space: nowrap;
}

.add-to-cart-btn:hover:not(:disabled) {
    background: var(--primary-hover);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px var(--ring);
}

.add-to-cart-btn:disabled {
    background: var(--muted);
    cursor: not-allowed;
    opacity: 0.7;
}

/* Loading State */
.loading-state {
    text-align: center;
    padding: 60px 20px;
    color: var(--subtext);
}

.spinner {
    width: 40px;
    height: 40px;
    margin: 0 auto 20px;
    border: 4px solid var(--card-border);
    border-top: 4px solid var(--primary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

/* Empty State */
.empty-products {
    text-align: center;
    padding: 60px 20px;
}

.empty-icon {
    font-size: 80px;
    margin-bottom: 20px;
    opacity: 0.5;
    color: var(--muted);
}

.empty-products h3 {
    margin: 0 0 10px 0;
    color: var(--heading);
}

.empty-products p {
    color: var(--subtext);
    margin-bottom: 20px;
}

.back-to-categories {
    padding: 10px 20px;
    background: var(--primary);
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.3s;
}

.back-to-categories:hover {
    background: var(--primary-hover);
}

/* Pagination */
.pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    margin-top: 30px;
    padding: 20px;
}

.pagination-btn {
    padding: 8px 16px;
    background: var(--bg);
    border: 2px solid var(--card-border);
    border-radius: 8px;
    font-size: 14px;
    color: var(--text);
    cursor: pointer;
    transition: all 0.3s ease;
}

.pagination-btn:hover:not(:disabled) {
    background: var(--ring);
    border-color: var(--primary);
}

.pagination-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.page-numbers {
    display: flex;
    gap: 4px;
}

.page-number {
    padding: 8px 14px;
    border-radius: 8px;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.3s ease;
    color: var(--subtext);
}

.page-number:hover:not(.disabled) {
    background: var(--bg);
}

.page-number.active {
    background: var(--primary);
    color: white;
    font-weight: 600;
}

.page-number.disabled {
    cursor: default;
    opacity: 0.5;
}

/* Product Modal */
.product-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--modal-overlay);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 3000;
    padding: 20px;
}

.product-modal-content {
    background: var(--card-bg);
    border-radius: 12px;
    width: 100%;
    max-width: 800px;
    max-height: 90vh;
    overflow: hidden;
    border: 1px solid var(--card-border);
}

.product-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid var(--card-border);
}

.product-modal-header h3 {
    margin: 0;
    color: var(--heading);
}

.close-modal-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: var(--subtext);
    transition: color 0.3s;
}

.close-modal-btn:hover {
    color: var(--orange-1);
}

.product-modal-body {
    display: grid;
    grid-template-columns: 300px 1fr;
    gap: 30px;
    padding: 20px;
    overflow-y: auto;
    max-height: calc(90vh - 80px);
}

.product-modal-image {
    background: var(--bg);
    border-radius: 8px;
    padding: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--card-border);
}

.product-modal-image img {
    max-width: 100%;
    max-height: 300px;
    object-fit: contain;
}

.product-modal-info h2 {
    margin: 0 0 15px;
    color: var(--heading);
}

.product-modal-price {
    font-size: 24px;
    font-weight: bold;
    color: var(--primary);
    margin-bottom: 15px;
}

.product-modal-stock {
    padding: 8px 12px;
    border-radius: 6px;
    display: inline-block;
    margin-bottom: 20px;
}

.product-modal-stock.in-stock {
    background: rgba(16, 185, 129, 0.15);
    color: var(--green);
}

.product-modal-stock.out-of-stock {
    background: rgba(249, 115, 22, 0.15);
    color: var(--orange-1);
}

.product-modal-details {
    margin-bottom: 20px;
}

.detail-row {
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
    border-bottom: 1px solid var(--card-border);
}

.detail-label {
    color: var(--subtext);
}

.detail-value {
    font-weight: bold;
    color: var(--heading);
}

.product-modal-description {
    margin-bottom: 30px;
}

.product-modal-description h4 {
    margin: 0 0 10px;
    color: var(--heading);
}

.product-modal-description p {
    color: var(--text);
    line-height: 1.6;
}

.add-to-cart-section {
    display: flex;
    gap: 20px;
    align-items: center;
}

.quantity-controls {
    display: flex;
    align-items: center;
    gap: 10px;
}

.qty-btn {
    background: var(--bg);
    border: 1px solid var(--card-border);
    width: 40px;
    height: 40px;
    border-radius: 4px;
    font-size: 18px;
    cursor: pointer;
    color: var(--text);
    transition: all 0.3s;
}

.qty-btn:hover {
    background: var(--ring);
    border-color: var(--primary);
}

.qty-input {
    width: 60px;
    text-align: center;
    padding: 10px;
    border: 1px solid var(--card-border);
    border-radius: 4px;
    font-size: 16px;
    background: var(--bg);
    color: var(--text);
}

.qty-input:focus {
    outline: none;
    border-color: var(--primary);
}

.add-to-cart-modal-btn {
    flex: 1;
    background: var(--primary);
    color: white;
    border: none;
    padding: 15px;
    border-radius: 6px;
    font-size: 16px;
    font-weight: bold;
    cursor: pointer;
    transition: background 0.3s;
}

.add-to-cart-modal-btn:hover {
    background: var(--primary-hover);
}

/* Checkout Modal */
.checkout-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--modal-overlay);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 4000;
    padding: 20px;
}

.checkout-modal-content {
    background: var(--card-bg);
    border-radius: 12px;
    width: 100%;
    max-width: 800px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid var(--card-border);
}

.checkout-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid var(--card-border);
    background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
    color: white;
}

.checkout-modal-header h3 {
    color: white;
    margin: 0;
}

.close-checkout-btn {
    background: rgba(255, 255, 255, 0.2);
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: white;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.3s;
}

.close-checkout-btn:hover {
    background: rgba(255, 255, 255, 0.3);
}

.checkout-modal-body {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
}

.invoice-summary {
    margin-bottom: 20px;
}

.summary-card {
    background: var(--bg);
    border-radius: 8px;
    padding: 15px;
    border: 1px solid var(--card-border);
}

.summary-row {
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
    border-bottom: 1px solid var(--card-border);
    color: var(--text);
}

.summary-row:last-child {
    border-bottom: none;
}

.summary-row.total {
    font-weight: bold;
    font-size: 18px;
}

.amount {
    color: var(--primary);
}

.cart-review {
    margin-bottom: 20px;
}

.cart-review h4 {
    margin: 0 0 10px;
    color: var(--heading);
}

.cart-review-items {
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid var(--card-border);
    border-radius: 8px;
}

.cart-review-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 15px;
    border-bottom: 1px solid var(--card-border);
}

.cart-review-item:last-child {
    border-bottom: none;
}

.item-info {
    display: flex;
    flex-direction: column;
}

.item-name {
    font-weight: bold;
    margin-bottom: 4px;
    color: var(--heading);
}

.item-quantity {
    font-size: 12px;
    color: var(--subtext);
}

.item-total {
    font-weight: bold;
    color: var(--primary);
}

.checkout-modal-footer {
    display: flex;
    gap: 10px;
    padding: 20px;
    border-top: 1px solid var(--card-border);
    background: var(--bg);
}

.cancel-btn {
    flex: 1;
    padding: 12px;
    background: var(--orange-1);
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.3s;
}

.cancel-btn:hover {
    background: var(--orange-2);
}

.submit-invoice-btn {
    flex: 2;
    padding: 12px;
    background: var(--green);
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    transition: background 0.3s;
}

.submit-invoice-btn:hover:not(:disabled) {
    background: #047857;
}

.submit-invoice-btn:disabled {
    background: var(--muted);
    cursor: not-allowed;
}

.spinner-small {
    width: 20px;
    height: 20px;
    border: 2px solid #ffffff;
    border-top: 2px solid transparent;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

/* Responsive */
@media (max-width: 768px) {
    .category-actions {
        flex-direction: column;
        align-items: stretch;
    }
    
    .product-content {
        flex-wrap: wrap;
    }
    
    .product-actions-right {
        min-width: 100%;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        order: 3;
    }
    
    .product-image-right {
        width: 60px;
        height: 60px;
        order: 2;
    }
    
    .product-details-left {
        order: 1;
        width: calc(100% - 76px);
    }
    
    .product-modal-body {
        grid-template-columns: 1fr;
        gap: 20px;
    }
    
    .product-modal-image {
        height: 200px;
    }
    
    .add-to-cart-section {
        flex-direction: column;
    }
    
    .cart-controls {
        flex-wrap: wrap;
    }
}
/* Product Modal - Compact & Professional */
.product-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 3000;
    padding: 20px;
    animation: fadeIn 0.3s ease;
}

.product-modal-content {
    background: var(--bg);
    border-radius: 16px;
    width: 100%;
    max-width: 750px;
    max-height: 85vh;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    animation: slideUp 0.3s ease;
}

.product-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
    border-bottom: 1px solid #f3f4f6;
    background: var(--card-bg);
}

.product-modal-header h3 {
    margin: 0;
    font-size: 17px;
    font-weight: 600;
    color: var(--text);
}

.close-modal-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #9ca3af;
    transition: all 0.3s ease;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.close-modal-btn:hover {
    background: #f3f4f6;
    color: #1a1a2e;
    transform: rotate(90deg);
}

.product-modal-body {
    display: flex;
    gap: 24px;
    padding: 24px;
    overflow-y: auto;
    max-height: calc(85vh - 70px);
}

.product-modal-image {
    flex: 0 0 200px;
    height: 200px;
    border-radius: 12px;
    overflow: hidden;
    background: #f8f9fa;
    border: 1px solid #f3f4f6;
    display: flex;
    align-items: center;
    justify-content: center;
}

.product-modal-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.product-modal-info {
    flex: 1;
    min-width: 0;
}

.product-modal-info h2 {
    font-size: 20px;
    font-weight: 700;
    color: #1a1a2e;
    margin: 0 0 8px 0;
    line-height: 1.3;
}

.product-modal-price {
    font-size: 22px;
    font-weight: 700;
    color: #059669;
    margin-bottom: 10px;
}

.product-modal-stock {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 14px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 14px;
}

.product-modal-stock.in-stock {
    background: #ecfdf5;
    color: #059669;
}

.product-modal-stock.out-of-stock {
    background: #fef2f2;
    color: #dc2626;
}

.stock-icon {
    font-weight: 700;
    font-size: 14px;
}

.product-modal-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px 16px;
    background: #f8f9fa;
    border-radius: 10px;
    padding: 12px 16px;
    margin-bottom: 12px;
}

.detail-row {
    display: flex;
    justify-content: space-between;
    padding: 4px 0;
    border-bottom: 1px solid #f3f4f6;
}

.detail-row:last-child {
    border-bottom: none;
}

.detail-label {
    font-size: 12px;
    color: #6b7280;
    font-weight: 500;
}

.detail-value {
    font-size: 13px;
    font-weight: 600;
    color: #1a1a2e;
}

.product-modal-description {
    background: #f8f9fa;
    border-radius: 10px;
    padding: 12px 16px;
    margin-bottom: 14px;
}

.product-modal-description p {
    font-size: 13px;
    color: #4b5563;
    line-height: 1.6;
    margin: 0;
}

/* Add to Cart Section */
.add-to-cart-section {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-top: 14px;
    border-top: 1px solid #f3f4f6;
}

.quantity-controls {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
}

.qty-btn {
    width: 34px;
    height: 34px;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    background: white;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #1a1a2e;
}

.qty-btn:hover {
    background: #f3f4f6;
    border-color: #4a6cf7;
}

.qty-btn.minus:hover {
    background: #fef2f2;
    border-color: #dc2626;
    color: #dc2626;
}

.qty-btn.plus:hover {
    background: #ecfdf5;
    border-color: #059669;
    color: #059669;
}

.qty-input {
    width: 50px;
    height: 34px;
    text-align: center;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 600;
    background: white;
    color: #1a1a2e;
}

.qty-input:focus {
    outline: none;
    border-color: #4a6cf7;
    box-shadow: 0 0 0 3px rgba(74, 108, 247, 0.1);
}

.add-to-cart-modal-btn {
    flex: 1;
    padding: 10px 20px;
    background: #4a6cf7;
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    white-space: nowrap;
}

.add-to-cart-modal-btn:hover {
    background: #3b5de7;
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(74, 108, 247, 0.3);
}

.add-to-cart-modal-btn:active {
    transform: translateY(0);
}

/* Animations */
@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(20px) scale(0.95);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

/* Responsive */
@media (max-width: 768px) {
    .product-modal-content {
        max-width: 95%;
        max-height: 90vh;
    }
    
    .product-modal-body {
        flex-direction: column;
        gap: 16px;
        padding: 16px;
    }
    
    .product-modal-image {
        flex: 0 0 160px;
        height: 160px;
        width: 100%;
    }
    
    .product-modal-info h2 {
        font-size: 18px;
    }
    
    .product-modal-price {
        font-size: 20px;
    }
    
    .product-modal-details {
        grid-template-columns: 1fr;
        gap: 2px;
    }
    
    .add-to-cart-section {
        flex-direction: column;
        gap: 10px;
    }
    
    .add-to-cart-modal-btn {
        width: 100%;
    }
    
    .quantity-controls {
        width: 100%;
        justify-content: center;
    }
}
.qty-input-cart {
    width: 50px;
    height: 28px;
    text-align: center;
    border: 2px solid #e5e7eb;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    background: white;
    color: #1a1a2e;
    padding: 0 4px;
}

/* Allow decimal input */
.qty-input-cart[type="number"] {
    -moz-appearance: textfield;
}

.qty-input-cart::-webkit-inner-spin-button,
.qty-input-cart::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

/* If you want to show the spin buttons for decimal input */
.qty-input-cart.show-spinner::-webkit-inner-spin-button,
.qty-input-cart.show-spinner::-webkit-outer-spin-button {
    -webkit-appearance: inner-spin-button;
    opacity: 1;
}
/* Add to your existing styles */
.cart-note-input {
    display: flex;
    align-items: center;
    margin-top: 6px;
    width: 100%;
    position: relative;
}

.note-input-field {
    width: 100%;
    padding: 4px 30px 4px 8px;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    font-size: 12px;
    direction: rtl;
    background: var(--bg);
    color: var(--text);
    transition: all 0.3s ease;
}

.note-input-field:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
    background: var(--bg);
}

.note-input-field::placeholder {
    color: #999;
    font-size: 11px;
}

.note-icon {
    position: absolute;
    left: 8px;
    font-size: 14px;
    opacity: 0.5;
    pointer-events: none;
}

/* Adjust the product actions right layout */
.product-actions-right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
    min-width: 180px;
}

.cart-controls {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

/* Mobile responsive */
@media (max-width: 768px) {
    .product-actions-right {
        min-width: 140px;
        width: 100%;
    }
    
    .cart-note-input {
        width: 100%;
    }
    
    .note-input-field {
        font-size: 11px;
        padding: 3px 25px 3px 6px;
    }
}
.category-image-wrapper {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
    border: 2px solid #e0e0e0;
    background: #f8f9fa;
    display: flex;
    align-items: center;
    justify-content: center;
}

.category-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.category-icon-large {
    font-size: 48px;
    width: 80px;
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f0f0f0;
    border-radius: 50%;
    flex-shrink: 0;
}

.category-info {
    display: flex;
    align-items: center;
    gap: 16px;
}

/* Responsive styles */
@media (max-width: 768px) {
    .category-image-wrapper {
        width: 60px;
        height: 60px;
    }
    
    .category-icon-large {
        font-size: 36px;
        width: 60px;
        height: 60px;
    }
}

@media (max-width: 480px) {
    .category-image-wrapper {
        width: 50px;
        height: 50px;
    }
    
    .category-icon-large {
        font-size: 28px;
        width: 50px;
        height: 50px;
    }
}

.product-gallery-frame { position: relative; }
.product-gallery-frame .gallery-arrow {
    position: absolute; z-index: 2; top: 50%; transform: translateY(-50%);
    width: 32px; height: 32px; border: 0; border-radius: 50%;
    background: rgba(0, 0, 0, .58); color: #fff; font-size: 22px; line-height: 1; cursor: pointer;
}
.product-gallery-frame .gallery-prev { left: 8px; }
.product-gallery-frame .gallery-next { right: 8px; }
.product-gallery-frame .gallery-count {
    position: absolute; z-index: 2; bottom: 8px; left: 50%; transform: translateX(-50%);
    padding: 2px 9px; border-radius: 12px; background: rgba(0, 0, 0, .6);
    color: #fff; font-size: 11px; direction: ltr;
}

.store-layout-large .category-header,
.store-layout-large .products-section { width: min(100% - 32px, 1100px); margin-inline: auto; }
.store-layout-large .category-header { padding: 22px; border: 1px solid var(--card-border); border-radius: 18px; background: var(--card-bg); }
.store-layout-large .products-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(100%, 320px), 1fr)); gap: 18px; }
.store-layout-large .product-list-item { padding: 10px; border: 1px solid var(--card-border); border-radius: 18px; background: var(--card-bg); box-shadow: 0 2px 10px var(--shadow-1); }
.store-layout-large .product-content { flex-direction: column; align-items: stretch; gap: 0; }
.store-layout-large .product-image-right { order: 0; width: 100%; height: auto; aspect-ratio: 1.55 / 1; border-radius: 14px; cursor: pointer; }
.store-layout-large .product-details-left { order: 1; padding: 14px 8px 4px; text-align: right; direction: rtl; }
.store-layout-large .product-title { font-size: 19px; line-height: 1.5; }
.store-layout-large .product-description { -webkit-line-clamp: 3; line-height: 1.7; text-align: right; }
.store-layout-large .product-meta-row { margin-top: 8px; display: flex; flex-wrap: nowrap; justify-content: space-between; gap: 8px; direction: rtl; text-align: right; overflow-x: auto; white-space: nowrap; }
.store-layout-large .product-meta-row span { flex: 0 0 auto; white-space: nowrap; text-align: right; }
.store-layout-large .product-actions-right { order: 2; width: 100%; min-width: 0; padding: 8px; align-items: stretch; }
.store-layout-large .cart-controls { display: flex; flex-direction: column; align-items: stretch; gap: 8px; }
.store-layout-large .cart-controls .add-to-cart-btn { min-height: 42px; }
.store-layout-large .product-modal-image { position: relative; }
@media (max-width: 600px) {
    .store-layout-large .category-header,
    .store-layout-large .products-section { width: calc(100% - 20px); }
    .store-layout-large .products-list { grid-template-columns: 1fr; gap: 12px; }
    .store-layout-large .product-list-item { padding: 9px; }
}

/* Compact category banner: readable heading first, controls in a single row. */
.category-header,
.store-layout-large .category-header {
    box-sizing: border-box;
    width: min(calc(100% - 24px), 1100px);
    margin: 12px auto 8px;
    padding: 16px;
    border: 1px solid var(--card-border);
    border-radius: 16px;
    background: var(--card-bg);
    box-shadow: 0 2px 10px var(--shadow-1);
}
.category-info { min-height: 56px; gap: 12px; margin: 0 0 12px; }
.category-icon-large,
.category-image-wrapper { width: 56px; height: 56px; flex: 0 0 56px; }
.category-details { min-width: 0; }
.category-details h1 { margin: 0 0 3px; color: var(--heading); font-size: 18px; line-height: 1.4; }
.category-details p { margin: 0; color: var(--subtext); font-size: 12px; }
.category-actions { display: flex; flex-direction: row; align-items: center; gap: 8px; }
.search-container { min-width: 0; flex: 1 1 80%; }
.search-input { box-sizing: border-box; height: 42px; padding: 9px 38px 9px 12px; border: 1px solid var(--card-border); background: var(--bg); }
.sort-btn { flex: 0 0 22%; justify-content: center; min-height: 42px; padding: 8px 6px; border: 0; background: var(--primary); color: #fff; white-space: nowrap; }
.sort-menu { top: calc(100% + 4px); }
@media (max-width: 480px) {
    .category-header,
    .store-layout-large .category-header { width: calc(100% - 20px); padding: 13px; }
    .category-info { min-height: 48px; margin-bottom: 10px; }
    .category-icon-large,
    .category-image-wrapper { width: 48px; height: 48px; flex-basis: 48px; }
    .category-details h1 { font-size: 16px; }
    .category-details p { font-size: 11px; }
    .category-actions { flex-direction: row; }
    .sort-btn { flex-basis: 24%; font-size: 12px; }
}
</style>
